import { atom, selector, RecoilState, RecoilValueReadOnly } from 'recoil';

export interface TileData {
  name: string;
  title: string;
  questions: string[];
  icon: string;
  color: string;
}

export interface FileUploadSettings {
  maxSizeMB: number;
  maxFiles: number;
  allowedExtensions: string[];
}

export interface ColorSettings {
  headerGradientStart: string;
  headerGradientEnd: string;
  sidebarBackground: string;
  submitButtonBackground: string;
}

export interface TitleSettings {
  headerTitle: string;
  welcomeTitle: string;
}

export interface UISettings {
  voiceMode: boolean;
  showThemeToggle: boolean;
  showLogoutButton: boolean;
  assistantMicEnabled?: boolean;
  colors?: ColorSettings;
  titles?: TitleSettings;
  fileUpload?: FileUploadSettings;
  tiles?: TileData[];
}

// Atom to store UI settings state
export const uiSettingsState: RecoilState<UISettings | null> = atom<UISettings | null>({
  key: 'uiSettings',
  default: null
});

// Atom to track loading state
export const uiSettingsLoadingState: RecoilState<boolean> = atom<boolean>({
  key: 'uiSettingsLoading',
  default: false
});

// Atom to track error state
export const uiSettingsErrorState: RecoilState<string | null> = atom<string | null>({
  key: 'uiSettingsError',
  default: null
});

// Selector to provide default file upload settings when UI settings are not available
export const fileUploadSettingsSelector: RecoilValueReadOnly<FileUploadSettings> = selector<FileUploadSettings>({
  key: 'fileUploadSettings',
  get: ({ get }) => {
    const uiSettings = get(uiSettingsState);
    
    // Return file upload settings from UI settings if available
    if (uiSettings?.fileUpload) {
      return uiSettings.fileUpload;
    }
    
    // Return default settings as fallback
    return {
      maxSizeMB: 25,
      maxFiles: 5,
      allowedExtensions: ['pdf', 'doc', 'docx', 'txt', 'json', 'csv', 'xlsx', 'jpg', 'jpeg', 'png']
    };
  }
});

// Selector to check if UI settings are initialized
export const uiSettingsInitializedSelector: RecoilValueReadOnly<boolean> = selector<boolean>({
  key: 'uiSettingsInitialized',
  get: ({ get }) => {
    const settings = get(uiSettingsState);
    const loading = get(uiSettingsLoadingState);
    const error = get(uiSettingsErrorState);
    
    // Settings are considered initialized if we have data, finished loading, or have an error
    return settings !== null || (!loading && error !== null);
  }
});